import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import {
  Buildings, Users, ShieldCheck, Export, MagnifyingGlass, ArrowRight,
  ChartBar, Target, EnvelopeSimple, Pulse, Sparkle, Heart
} from '@phosphor-icons/react';
import ProfileHealthWidget from '../components/ProfileHealthWidget';
import ScanSourcesWidget from '../components/ScanSourcesWidget';
import DataQualityWidget from '../components/DataQualityWidget';
import MustContainStatsWidget from '../components/MustContainStatsWidget';
import CollapsibleSection from '../components/CollapsibleSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_NAMES = {
  NL: 'Nederland', BE: 'Belgie', FR: 'Frankrijk', DE: 'Duitsland', GB: 'VK',
  IE: 'Ierland', LU: 'Luxemburg', AT: 'Oostenrijk', CH: 'Zwitserland', ES: 'Spanje',
  PT: 'Portugal', IT: 'Italie', GR: 'Griekenland', DK: 'Denemarken', SE: 'Zweden',
  NO: 'Noorwegen', FI: 'Finland', PL: 'Polen', CZ: 'Tsjechie', HU: 'Hongarije',
};

const COUNTRY_FLAGS = { NL: 'NL', BE: 'BE', FR: 'FR', DE: 'DE' };
const COUNTRY_COLORS = {
  NL: 'border-l-4 border-l-orange-500',
  BE: 'border-l-4 border-l-yellow-500',
  FR: 'border-l-4 border-l-blue-500',
  DE: 'border-l-4 border-l-red-500',
  GB: 'border-l-4 border-l-indigo-500',
  IE: 'border-l-4 border-l-emerald-500',
  LU: 'border-l-4 border-l-cyan-500',
  AT: 'border-l-4 border-l-red-400',
  CH: 'border-l-4 border-l-red-600',
  ES: 'border-l-4 border-l-yellow-600',
  PT: 'border-l-4 border-l-green-600',
  IT: 'border-l-4 border-l-green-500',
  GR: 'border-l-4 border-l-sky-500',
  MT: 'border-l-4 border-l-amber-500',
  CY: 'border-l-4 border-l-orange-400',
  DK: 'border-l-4 border-l-rose-500',
  SE: 'border-l-4 border-l-blue-400',
  NO: 'border-l-4 border-l-blue-700',
  FI: 'border-l-4 border-l-blue-300',
  IS: 'border-l-4 border-l-sky-600',
  PL: 'border-l-4 border-l-rose-400',
  CZ: 'border-l-4 border-l-blue-600',
  SK: 'border-l-4 border-l-blue-500',
  HU: 'border-l-4 border-l-green-700',
  RO: 'border-l-4 border-l-yellow-400',
  BG: 'border-l-4 border-l-green-400',
  HR: 'border-l-4 border-l-red-500',
  SI: 'border-l-4 border-l-blue-500',
  RS: 'border-l-4 border-l-red-700',
  BA: 'border-l-4 border-l-yellow-500',
  ME: 'border-l-4 border-l-amber-600',
  MK: 'border-l-4 border-l-red-400',
  AL: 'border-l-4 border-l-red-600',
  XK: 'border-l-4 border-l-blue-400',
  EE: 'border-l-4 border-l-sky-400',
  LV: 'border-l-4 border-l-rose-600',
  LT: 'border-l-4 border-l-yellow-500',
  UA: 'border-l-4 border-l-sky-500',
  MD: 'border-l-4 border-l-blue-500',
  US: 'border-l-4 border-l-violet-600',
};

export default function DashboardPage() {
  const { user } = useAuth();
  const { active: activeProfile } = useProfile();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [countries, setCountries] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Email-by-country is heavy; race it against a 5s timeout so the dashboard
    // can render without it (it loads asynchronously into a state that updates
    // the country cards when it eventually resolves).
    const emailStatsWithTimeout = Promise.race([
      fetch(`${API}/stats/email-by-country`, { credentials: 'include' })
        .then(r => r.json())
        .catch(() => null),
      new Promise((resolve) => setTimeout(() => resolve(null), 5000)),
    ]);
    Promise.all([
      fetch(`${API}/stats`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/countries`, { credentials: 'include' }).then(r => r.json()),
      emailStatsWithTimeout,
    ]).then(([s, c, e]) => {
      setStats(s);
      setCountries(c);
      setEmailStats(e);
    }).finally(() => setLoading(false));
    // If the heavy call eventually resolves later, also patch state
    fetch(`${API}/stats/email-by-country`, { credentials: 'include' })
      .then(r => r.json())
      .then(e => setEmailStats(e))
      .catch(() => {});
  }, [activeProfile]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Facilities', value: stats?.total_facilities || 0, icon: Buildings, color: 'text-[#0055FF]' },
    { label: 'Total Contacts', value: stats?.total_contacts || 0, icon: Users, color: 'text-[#0055FF]' },
    { label: 'Verified Contacts', value: stats?.verified_contacts || 0, icon: ShieldCheck, color: 'text-emerald-600' },
    { label: 'Exportable', value: stats?.exportable || 0, icon: Export, color: 'text-[#0055FF]' },
    { label: 'Avg Lead Score', value: stats?.avg_lead_score || 0, icon: Target, color: 'text-[#0055FF]' },
  ];

  const typeLabels = {
    municipal: 'Municipal', private: 'Private', sports_complex: 'Sports Complex',
    aquatic_center: 'Aquatic Center', swim_club: 'Swim Club', school: 'School',
    wellness: 'Wellness', hotel_pool: 'Hotel Pool'
  };

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-black tracking-tight">Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Welcome back{user?.name ? `, ${user.name}` : ''}. Here is your lead generation overview.
        </p>
      </div>

      {/* Profile health widget — per-profiel Graph status + emails today + last sync */}
      <CollapsibleSection id="profile-health" title="Profielen health" icon={Heart} accent="text-rose-500" storageKey="dash:collapsedSections">
        <ProfileHealthWidget />
      </CollapsibleSection>

      {/* Data Quality Health Check — per-profiel data-kwaliteit thermometer */}
      <CollapsibleSection id="data-quality" title="Data-kwaliteit per profiel" icon={Sparkle} accent="text-indigo-500" storageKey="dash:collapsedSections">
        <DataQualityWidget />
      </CollapsibleSection>

      {/* Must-Contain Gate stats — per-profiel relevantie-filter monitor */}
      <CollapsibleSection id="must-contain" title="Relevantie-gate (must_contain)" icon={Target} accent="text-violet-600" storageKey="dash:collapsedSections">
        <MustContainStatsWidget />
      </CollapsibleSection>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-zinc-200 border border-zinc-200 mb-8" data-testid="stats-grid">
        {statCards.map((s, i) => (
          <div key={i} className="bg-white p-4">
            <div className="flex items-center gap-2 mb-2">
              <s.icon size={16} weight="bold" className={s.color} />
              <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{s.label}</span>
            </div>
            <div className="font-data text-2xl font-bold">{s.value}</div>
          </div>
        ))}
      </div>

      {/* External scan sources quota — per profile, with pause toggles */}
      <CollapsibleSection id="scan-sources" title="Externe Scan Bronnen" icon={Pulse} accent="text-emerald-600" storageKey="dash:collapsedSections">
        <ScanSourcesWidget />
      </CollapsibleSection>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold">Countries</h2>
          <button
            data-testid="view-all-facilities-btn"
            onClick={() => navigate('/facilities')}
            className="text-xs font-medium text-[#0055FF] hover:underline flex items-center gap-1"
          >
            View all facilities <ArrowRight size={12} />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200" data-testid="countries-grid">
          {countries.map(c => {
            const eStats = emailStats?.countries?.find(e => e.country_code === c.code);
            return (
            <div
              key={c.code}
              data-testid={`country-card-${c.code}`}
              className={`bg-white p-5 cursor-pointer hover:bg-zinc-50 transition-colors duration-150 ${COUNTRY_COLORS[c.code] || 'border-l-4 border-l-zinc-300'}`}
              onClick={() => navigate(`/facilities?country=${c.code}`)}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-data text-lg font-bold text-zinc-400">{c.code}</span>
                {eStats && eStats.response_rate > 0 && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 ${eStats.response_rate > 10 ? 'bg-emerald-100 text-emerald-700' : eStats.response_rate > 5 ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>
                    {eStats.response_rate}%
                  </span>
                )}
              </div>
              <h3 className="font-heading text-lg font-bold mb-1">{c.name}</h3>
              <p className="text-xs text-zinc-500 mb-4">{c.name_en}</p>
              <div className="flex gap-6">
                <div>
                  <div className="font-data text-xl font-bold">{c.facility_count}</div>
                  <div className="text-xs text-zinc-500">Facilities</div>
                </div>
                <div>
                  <div className="font-data text-xl font-bold">{c.contact_count}</div>
                  <div className="text-xs text-zinc-500">Contacts</div>
                </div>
              </div>
              {eStats && eStats.sent > 0 && (
                <div className="flex gap-4 mt-3 pt-3 border-t border-zinc-100">
                  <div>
                    <div className="font-data text-sm font-bold text-[#0055FF]">{eStats.sent}</div>
                    <div className="text-[10px] text-zinc-400">Verzonden</div>
                  </div>
                  <div>
                    <div className="font-data text-sm font-bold text-emerald-600">{eStats.replies}</div>
                    <div className="text-[10px] text-zinc-400">Antwoorden</div>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Email Stats per Country */}
      {emailStats && emailStats.countries?.length > 0 && (
        <div className="mb-8" data-testid="email-country-stats">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl font-semibold">Email Effectiviteit per Land</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Verzonden emails en antwoorden per land</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <span>Totaal verzonden: <strong className="text-zinc-800">{emailStats.totals?.sent}</strong></span>
              <span>Totaal antwoorden: <strong className="text-emerald-600">{emailStats.totals?.replies}</strong></span>
              <span>Response rate: <strong className={emailStats.totals?.response_rate > 5 ? 'text-emerald-600' : 'text-amber-600'}>{emailStats.totals?.response_rate}%</strong></span>
            </div>
          </div>
          <div className="border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Land</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Verzonden</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Antwoorden</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Response Rate</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500 w-48">Effectiviteit</th>
                </tr>
              </thead>
              <tbody>
                {emailStats.countries.map(c => {
                  const barWidth = emailStats.totals?.sent > 0 ? Math.round(c.sent / emailStats.totals.sent * 100) : 0;
                  return (
                    <tr key={c.country_code} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`email-stat-${c.country_code}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-data text-sm font-bold text-zinc-400 w-6">{c.country_code}</span>
                          <span className="text-sm font-medium">{COUNTRY_NAMES[c.country_code] || c.country_code}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-data font-bold">{c.sent}</td>
                      <td className="px-4 py-2.5 text-right font-data font-bold text-emerald-600">{c.replies}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`font-data font-bold ${c.response_rate > 10 ? 'text-emerald-600' : c.response_rate > 5 ? 'text-amber-600' : 'text-zinc-400'}`}>
                          {c.response_rate}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-zinc-100 overflow-hidden">
                            <div className="h-full bg-[#0055FF]" style={{ width: `${barWidth}%` }} />
                          </div>
                          <span className="text-[10px] text-zinc-400 w-8">{barWidth}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Type Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5">
          <h3 className="font-heading text-lg font-semibold mb-4">Facility Types</h3>
          <div className="space-y-2">
            {stats?.by_type && Object.entries(stats.by_type).filter(([,v]) => v > 0).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between py-1.5 border-b border-zinc-100 last:border-0">
                <span className="text-sm">{typeLabels[type] || type}</span>
                <span className="font-data text-sm font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white p-5">
          <h3 className="font-heading text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-2">
            {[
              { label: 'Start New Scan', path: '/scans', icon: MagnifyingGlass, testId: 'quick-start-scan' },
              { label: 'Email Marketing', path: '/email', icon: EnvelopeSimple, testId: 'quick-email' },
              { label: 'Export to Exact', path: '/exports', icon: Export, testId: 'quick-export' },
              { label: 'Lead Scores', path: '/lead-scores', icon: ChartBar, testId: 'quick-lead-scores' },
            ].map(a => (
              <button
                key={a.path}
                data-testid={a.testId}
                onClick={() => navigate(a.path)}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium border border-zinc-200 rounded-sm hover:bg-zinc-50 hover:border-zinc-300 transition-colors duration-150 text-left"
              >
                <a.icon size={18} weight="bold" className="text-[#0055FF]" />
                {a.label}
                <ArrowRight size={14} className="ml-auto text-zinc-400" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
